import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { CLBadge } from "@/components/Badges";
import { SignalBadge } from "@/components/SignalBadge";
import { SoWhat } from "@/components/SoWhat";
import { TierGate } from "@/components/TierGate";
import { ExclusiveGate } from "@/components/ExclusiveGate";
import { RichText } from "@/components/RichText";
import DOMPurify from "dompurify";
import { ShareRow } from "@/components/ShareRow";
import { Seo } from "@/components/Seo";
import { timeAgo } from "@/lib/format";
import { ArrowLeft, ExternalLink, Clock, Calendar } from "lucide-react";

const LEVELS = [
  ["what_happened", "What Happened", "01"],
  ["why_it_matters", "Why It Matters", "02"],
  ["market_impact", "Market Impact", "03"],
  ["data_points", "The Data", "04"],
  ["cryptolyfe_view", "OnchainLyfe View", "05"],
  ["what_watching", "What We're Watching", "06"],
];

function readingMinutes(a) {
  const parts = [a.body, a.summary, ...LEVELS.map(([k]) => a[k])].filter(Boolean);
  const words = parts.join(" ").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

function RelatedCard({ a }) {
  return (
    <Link to={`/article/${a.slug}`} data-testid={`related-card-${a.slug}`} className="group block">
      <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-white/5 border border-white/10 mb-3">
        {a.hero_image ? (
          <img src={a.hero_image} alt={a.title} loading="lazy" className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center"><span className="font-display text-2xl font-black text-white/10">OnchainLyfe</span></div>
        )}
      </div>
      <p className="font-mono-cl text-[10px] tracking-[0.16em] uppercase text-[#ccff00]">{a.category}</p>
      <h4 className="font-display text-base font-bold leading-snug mt-1 group-hover:text-[#ccff00] transition-colors line-clamp-3">{a.title}</h4>
      <p className="text-[11px] text-zinc-500 mt-1.5">{timeAgo(a.published_at)}</p>
    </Link>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false); setRelated([]);
    client.get(`/articles/${slug}?lang=${lang}`).then((r) => setA(r.data)).catch(() => setErr(true));
    client.get(`/articles/${slug}/related?lang=${lang}`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
    window.scrollTo(0, 0);
  }, [slug, lang]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-400">Article not found. <Link to="/news" className="text-[#ccff00]">Back to News</Link></div>;
  if (!a) return (
    <div className="max-w-[760px] mx-auto px-5 sm:px-6 py-12 animate-pulse">
      <div className="h-3 w-24 bg-white/10 rounded mb-6" />
      <div className="h-10 w-full bg-white/10 rounded mb-3" />
      <div className="h-10 w-3/4 bg-white/10 rounded mb-6" />
      <div className="aspect-[16/9] w-full bg-white/5 rounded-2xl mb-8" />
      <div className="space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-4 w-full bg-white/10 rounded" />)}</div>
    </div>
  );

  const mins = readingMinutes(a);
  const canonical = typeof window !== "undefined" ? window.location.href : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "NewsArticle",
    headline: a.title, description: a.summary || a.title,
    image: a.hero_image ? [a.hero_image] : undefined,
    datePublished: a.published_at, dateModified: a.updated_at || a.published_at,
    author: [{ "@type": a.analyst_name ? "Person" : "Organization", name: a.analyst_name || a.author || "OnchainLyfe Editorial" }],
    publisher: { "@type": "Organization", name: "OnchainLyfe" },
    articleSection: a.category, mainEntityOfPage: canonical,
  };
  const showUpdated = a.updated_at && a.published_at && Math.abs(new Date(a.updated_at) - new Date(a.published_at)) > 3600000;

  return (
    <article className="max-w-[760px] mx-auto px-5 sm:px-6 py-10 sm:py-12">
      <Seo title={`${a.title} · OnchainLyfe`} description={a.summary || a.title} image={a.hero_image} url={canonical}
        article={{ published: a.published_at, modified: a.updated_at, section: a.category }} jsonLd={jsonLd} />

      <Link to="/news" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#ccff00] mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      {/* Kicker / category */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-mono-cl text-[11px] tracking-[0.24em] uppercase text-[#ccff00]" data-testid="article-category">{a.category}</span>
        {a.region && a.region !== "global" && <span className="font-mono-cl text-[11px] tracking-[0.24em] uppercase text-zinc-500">· {a.region}</span>}
        {(a.badges || []).slice(0, 2).map((b) => <CLBadge key={b} label={b} />)}
        {a.source_badge && <CLBadge label={a.source_badge} />}
      </div>

      {/* Headline */}
      <h1 data-testid="article-headline" className="font-display font-black tracking-tight leading-[1.08] text-[2rem] sm:text-[2.75rem] lg:text-[3.25rem]">
        {a.title}
      </h1>

      {/* Excerpt */}
      {a.summary && <p className="text-lg sm:text-xl text-zinc-300 mt-4 sm:mt-5 leading-relaxed">{a.summary}</p>}

      {/* Byline + meta */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-6 text-sm text-zinc-400">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#ccff00]/15 grid place-items-center font-display font-bold text-[#ccff00] text-sm">
            {(a.analyst_name || a.author || "C").slice(0, 1)}
          </div>
          <span className="text-zinc-200 font-medium">
            {a.analyst_name ? <>By <Link to="/analysts" className="text-[#ccff00] hover:underline">{a.analyst_name}</Link></> : `By ${a.author || "OnchainLyfe Editorial"}`}
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(a.published_at, lang)}</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {mins} min read</span>
        {a.signal_score != null && <SignalBadge score={a.signal_score} size="sm" />}
      </div>
      {showUpdated && <p className="text-xs text-zinc-500 mt-2">Last updated {fmtDate(a.updated_at, lang)}</p>}

      {/* Share */}
      <div className="mt-5 pb-6 border-b border-white/10">
        <ShareRow title={a.title} summary={a.summary} url={canonical} />
      </div>

      {/* Hero */}
      {a.hero_image && (
        <figure className="my-8">
          <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[16/9] bg-white/5">
            <img src={a.hero_image} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
          </div>
          {a.hero_caption && <figcaption className="text-xs text-zinc-500 mt-2 text-center">{a.hero_caption}</figcaption>}
        </figure>
      )}

      <SoWhat text={a.so_what} watchNext={a.watch_next} />

      {a.locked ? (
        a.blur_preview ? (
          <div className="my-8"><ExclusiveGate preview={a.preview} analystName={a.analyst_name} analystSlug={a.analyst_slug} label="research" /></div>
        ) : (
          <div className="my-8"><TierGate tier={a.tier} /></div>
        )
      ) : (
        <>
          {a.live_updates && a.live_updates.length > 0 && (
            <div className="rounded-2xl border border-white/10 p-5 sm:p-6 my-8">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-[#ccff00]" />
                <span className="font-mono-cl text-xs tracking-[0.24em] uppercase text-[#ccff00]">Live Updates</span>
              </div>
              <div className="space-y-3">
                {a.live_updates.map((u, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="font-mono-cl text-xs text-zinc-500 shrink-0 w-14">{u.time}</span>
                    <span className="text-sm text-zinc-200">{u.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Editorial sections */}
          <div className="my-10 space-y-9">
            {LEVELS.map(([key, label, num]) =>
              a[key] ? (
                <section key={key} data-testid={`article-section-${key}`}>
                  <div className="flex items-baseline gap-3 mb-2.5">
                    <span className="hidden sm:block font-display text-2xl font-black text-white/[0.08] leading-none">{num}</span>
                    <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00]">{label}</p>
                  </div>
                  <RichText text={a[key]} />
                </section>
              ) : null
            )}
          </div>

          {a.body && (
            /<\/?[a-z][\s\S]*>/i.test(a.body) ? (
              <div className="article-prose my-8" data-testid="article-body-html"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.body, { ADD_ATTR: ["target", "loading"] }) }} />
            ) : (
              <div className="my-8"><RichText text={a.body} /></div>
            )
          )}

          {a.source_url && (
            <a href={a.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm text-[#ccff00] hover:underline">
              View primary source <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </>
      )}

      {/* Footer share */}
      <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between gap-4">
        <span className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-500">Share this story</span>
        <ShareRow title={a.title} summary={a.summary} url={canonical} compact />
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-14" data-testid="related-stories">
          <h3 className="font-display text-2xl font-bold mb-6">{lang === "id" ? "Berita Terkait" : "Related Stories"}</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {related.map((r) => <RelatedCard key={r.slug} a={r} />)}
          </div>
        </section>
      )}
    </article>
  );
}
